import { useState, useEffect, useRef, useCallback } from '@wordpress/element';
import ProductCard from '../../product-grid/components/ProductCard';

function ArrowBtn( { dir, onClick, disabled, style } ) {
	return (
		<button
			className={ `mwsb-slider-arrow mwsb-slider-arrow--${ dir }${ disabled ? ' mwsb-slider-arrow--disabled' : '' }` }
			onClick={ onClick }
			aria-label={ dir === 'prev' ? 'Previous' : 'Next' }
			disabled={ disabled }
			style={ style }
		>
			{ dir === 'prev' ? (
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
					<path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			) : (
				<svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
					<path d="M7.5 5L12.5 10L7.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
				</svg>
			) }
		</button>
	);
}

export default function SliderTrack( { attributes, products } ) {
	const {
		columnsDesktop,
		columnsTablet,
		columnsMobile,
		gap,
		showArrows,
		arrowPosition,
		arrowSize,
		arrowBgColor,
		arrowIconColor,
		arrowBorderColor,
		arrowBorderRadius,
		showDots,
		dotSize,
		dotColor,
		dotActiveColor,
		dotGap,
		autoplay,
		autoplaySpeed,
	} = attributes;

	const trackRef    = useRef( null );
	const autoplayRef = useRef( null );
	const pausedRef   = useRef( false );

	const [ activeIndex, setActiveIndex ] = useState( 0 );
	const total = products.length;

	const getCardWidth = useCallback( () => {
		const track = trackRef.current;
		if ( ! track || ! track.firstChild ) return 0;
		return track.firstChild.getBoundingClientRect().width + gap;
	}, [ gap ] );

	const scrollToIndex = useCallback( ( index ) => {
		const track = trackRef.current;
		if ( ! track ) return;
		const cardWidth = getCardWidth();
		track.scrollTo( { left: cardWidth * index, behavior: 'smooth' } );
		setActiveIndex( index );
	}, [ getCardWidth ] );

	const prev = useCallback( () => {
		scrollToIndex( Math.max( activeIndex - 1, 0 ) );
	}, [ activeIndex, scrollToIndex ] );

	const next = useCallback( () => {
		scrollToIndex( Math.min( activeIndex + 1, total - 1 ) );
	}, [ activeIndex, total, scrollToIndex ] );

	useEffect( () => {
		const track = trackRef.current;
		if ( ! track ) return;

		const onScroll = () => {
			const cardWidth = getCardWidth();
			if ( ! cardWidth ) return;
			const index = Math.round( track.scrollLeft / cardWidth );
			setActiveIndex( Math.max( 0, Math.min( index, total - 1 ) ) );
		};

		track.addEventListener( 'scroll', onScroll, { passive: true } );
		return () => track.removeEventListener( 'scroll', onScroll );
	}, [ getCardWidth, total ] );

	useEffect( () => {
		if ( ! autoplay ) return;

		autoplayRef.current = setInterval( () => {
			if ( pausedRef.current ) return;
			setActiveIndex( ( prev ) => {
				const next = prev + 1 >= total ? 0 : prev + 1;
				const track = trackRef.current;
				if ( track ) {
					const cardWidth = getCardWidth();
					track.scrollTo( { left: cardWidth * next, behavior: 'smooth' } );
				}
				return next;
			} );
		}, autoplaySpeed );

		return () => clearInterval( autoplayRef.current );
	}, [ autoplay, autoplaySpeed, total, getCardWidth ] );

	const isOutside = arrowPosition === 'outside';

	const trackStyle = {
		'--mwsb-slider-cols':        columnsDesktop,
		'--mwsb-slider-cols-tablet': columnsTablet,
		'--mwsb-slider-cols-mobile': columnsMobile,
		'--mwsb-slider-gap':         `${ gap }px`,
	};

	const arrowStyle = {
		width:        arrowSize         ? `${ arrowSize }px`         : undefined,
		height:       arrowSize         ? `${ arrowSize }px`         : undefined,
		background:   arrowBgColor      || undefined,
		color:        arrowIconColor    || undefined,
		borderColor:  arrowBorderColor  || undefined,
		borderRadius: arrowBorderRadius != null ? `${ arrowBorderRadius }px` : undefined,
	};

	const dotBaseStyle   = { width: dotSize ? `${ dotSize }px` : undefined, height: dotSize ? `${ dotSize }px` : undefined, background: dotColor       || undefined };
	const dotActiveStyle = { width: dotSize ? `${ dotSize }px` : undefined, height: dotSize ? `${ dotSize }px` : undefined, background: dotActiveColor || undefined };

	const track = (
		<div
			className="mwsb-slider-track"
			ref={ trackRef }
			style={ trackStyle }
		>
			{ products.map( ( product ) => (
				<div key={ product.id } className="mwsb-slider-card">
					<ProductCard
						product={ product }
						settings={ attributes }
					/>
				</div>
			) ) }
		</div>
	);

	return (
		<div
			className={ `mwsb-slider-wrapper mwsb-arrows-${ arrowPosition }` }
			onMouseEnter={ () => { pausedRef.current = true; } }
			onMouseLeave={ () => { pausedRef.current = false; } }
		>
			{ showArrows && isOutside && (
				<ArrowBtn
					dir="prev"
					onClick={ prev }
					disabled={ activeIndex === 0 }
					style={ arrowStyle }
				/>
			) }

			<div className="mwsb-slider-inner">
				{ showArrows && ! isOutside && (
					<ArrowBtn
						dir="prev"
						onClick={ prev }
						disabled={ activeIndex === 0 }
						style={ arrowStyle }
					/>
				) }

				{ track }

				{ showArrows && ! isOutside && (
					<ArrowBtn
						dir="next"
						onClick={ next }
						disabled={ activeIndex >= total - 1 }
						style={ arrowStyle }
					/>
				) }
			</div>

			{ showArrows && isOutside && (
				<ArrowBtn
					dir="next"
					onClick={ next }
					disabled={ activeIndex >= total - 1 }
					style={ arrowStyle }
				/>
			) }

			{ showDots && total > 1 && (
				<div
					className="mwsb-slider-dots"
					style={ { gap: dotGap ? `${ dotGap }px` : undefined } }
				>
					{ products.map( ( _, i ) => (
						<button
							key={ i }
							className={ `mwsb-slider-dot${ i === activeIndex ? ' mwsb-slider-dot--active' : '' }` }
							onClick={ () => scrollToIndex( i ) }
							aria-label={ `Go to slide ${ i + 1 }` }
							style={ i === activeIndex ? dotActiveStyle : dotBaseStyle }
						/>
					) ) }
				</div>
			) }
		</div>
	);
}
